/**
 * Advertising opt-out state for this browser (owner + counsel work, 2026-10-14; policy section 7).
 * A visitor is opted out of the sale/sharing of personal information for advertising when EITHER
 *   - the browser sends Global Privacy Control (navigator.globalPrivacyControl === true), which we honor for every visitor, or
 *   - they chose "Opt out" on /privacy-choices, stored as the first-party cookie `rq_ad_optout=1` (12 months).
 * Opted-out browsers never load the advertising tags (Meta Pixel, HubSpot tracking and ads) and GA4 runs with
 * Google's ad features denied (see src/components/privacy/AdTagGate.tsx and the GA4 snippet in src/app/layout.tsx).
 * The same file lives in rebeliq-website/src/lib/privacy-optout.ts; keep them identical.
 */
export const OPT_OUT_COOKIE = 'rq_ad_optout';
const YEAR = 60 * 60 * 24 * 365;

/** Inline-script version of isAdOptedOut(), for snippets that run before React (keep in step with the functions below). */
export const OPT_OUT_INLINE = `(navigator.globalPrivacyControl===true||/(?:^|; )${OPT_OUT_COOKIE}=1(?:;|$)/.test(document.cookie))`;

export function hasGpc(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

export function hasOptOutCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return new RegExp(`(?:^|; )${OPT_OUT_COOKIE}=1(?:;|$)`).test(document.cookie);
}

export function isAdOptedOut(): boolean {
  return hasGpc() || hasOptOutCookie();
}

/** First-party advertising and marketing-tracking cookies we can clear on our own domain when someone opts out. */
const AD_COOKIES = ['_fbp', '_fbc', '_gcl_au', '_gcl_aw', '_gcl_dc', 'hubspotutk', '__hstc', '__hssc', '__hssrc', 'li_fat_id', '_uetsid', '_uetvid'];

function expireCookie(name: string) {
  const host = location.hostname;
  const parts = host.split('.');
  const domains = ['', host, parts.length > 1 ? '.' + parts.slice(-2).join('.') : host];
  for (const d of domains) {
    document.cookie = `${name}=; Max-Age=0; Path=/${d ? `; Domain=${d}` : ''}`;
  }
}

/** Record this browser's choice. Opting out also clears first-party ad cookies so already-set identifiers stop being sent. */
export function setAdOptOut(optOut: boolean) {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  if (optOut) {
    document.cookie = `${OPT_OUT_COOKIE}=1; Max-Age=${YEAR}; Path=/; SameSite=Lax${secure}`;
    AD_COOKIES.forEach(expireCookie);
  } else {
    document.cookie = `${OPT_OUT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
  }
}

/** HubSpot's visitor cookie, when present: lets us apply a GPC or toggle opt-out to a known contact record (policy section 7). */
export function readHubspotUtk(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|; )hubspotutk=([a-f0-9]{16,64})(?:;|$)/i);
  return m ? m[1] : null;
}
